import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import HiveFormDialog from "@/components/HiveFormDialog";
import HiveCoKeepers from "@/components/HiveCoKeepers";
import ShareLinkDialog from "@/components/ShareLinkDialog";
import CommentsThread from "@/components/CommentsThread";
import HiveTrendsTab from "@/components/HiveTrendsTab";
import TreatmentsTab from "@/components/TreatmentsTab";
import HiveWeatherTab from "@/components/HiveWeatherTab";
import HiveLocationCard from "@/components/HiveLocationCard";
import VoiceJournalRecorder from "@/components/VoiceJournalRecorder";
import { climateZoneForLat, zoneStyle } from "@/lib/climate";
import { ArrowLeft, Plus, MapPin, Crown, Calendar, Trash2, Image as ImageIcon, Pencil, Bug } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { uploadToCloudinary } from "@/lib/uploads";
import VideoUploadField from "@/components/VideoUploadField";
import VideoLightbox from "@/components/VideoLightbox";

export default function HiveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [hive, setHive] = useState(null);
  const [allHives, setAllHives] = useState([]);
  const [journals, setJournals] = useState([]);
  const [journalOpen, setJournalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingJournal, setEditingJournal] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  // ?tab=trends|weather|journal|photos|treatments and optional ?month=YYYY-MM
  // come from deep-links (e.g. sparkline click on Hives page).
  const tabFromUrl = searchParams.get("tab");
  const monthFromUrl = searchParams.get("month");
  const validTabs = ["journal", "photos", "treatments", "trends", "weather"];
  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : "journal";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Keep tab in sync if the URL changes (eg. clicking a different sparkline)
  useEffect(() => {
    if (validTabs.includes(tabFromUrl)) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onTabChange = (v) => {
    setActiveTab(v);
    // Drop the deep-link params once the user navigates manually so the URL stays clean.
    if (tabFromUrl || monthFromUrl) {
      setSearchParams({}, { replace: true });
    }
  };

  const isOwner = hive && user && hive.user_id === user.id;
  // role for THIS user on THIS hive: owner if owner, otherwise we infer from member list — but for now treat any non-owner as editor (membership endpoints will enforce server-side); CommentsThread receives role to gate UI
  const role = isOwner ? "owner" : "editor"; // best-effort default; backend enforces actual role

  const load = async () => {
    const [h, j, hs] = await Promise.all([
      api.get(`/hives/${id}`),
      api.get("/journals", { params: { hive_id: id } }),
      api.get("/hives"),
    ]);
    setHive(h.data);
    setJournals(j.data);
    setAllHives(hs.data);
  };

  useEffect(() => { load(); }, [id]);

  const del = async () => {
    if (!confirm("Delete this hive and all its entries?")) return;
    await api.delete(`/hives/${id}`);
    toast.success("Hive removed");
    navigate("/app/hives");
  };

  const deleteJournal = async (jid) => {
    if (!confirm("Delete this inspection entry? This can't be undone.")) return;
    try {
      await api.delete(`/journals/${jid}`);
      toast.success("Entry deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  // The author, hive owner, or an editor can edit/delete an entry (backend enforces).
  const canEditEntry = (j) => isOwner || role === "editor" || j.user_id === user?.id;

  if (!hive) return <div className="text-stone-500">Loading…</div>;

  const allPhotos = journals.flatMap((j) => (j.photos || []).map((p) => ({ url: p, journal: j })));

  return (
    <div className="space-y-8" data-testid="hive-detail-page">
      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/app/hives" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800" data-testid="back-to-hives">
            <ArrowLeft className="w-4 h-4" /> All hives
          </Link>
          {allHives.length > 1 && (
            <Select value={hive.id} onValueChange={(v) => { if (v !== hive.id) navigate(`/app/hives/${v}`); }}>
              <SelectTrigger className="w-auto gap-2 rounded-full bg-white border-stone-200 h-9 text-sm" data-testid="hive-switch-select">
                <span className="text-xs uppercase tracking-[0.2em] text-stone-500">Switch</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {allHives.map((h) => (
                  <SelectItem key={h.id} value={h.id} data-testid={`hive-switch-item-${h.id}`}>
                    {h.name}{h.location ? ` · ${h.location}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Hive</div>
            <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">{hive.name}</h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-stone-500 flex-wrap">
              {(hive.weather_location_label || hive.location) && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {hive.weather_location_label || hive.location}
                </span>
              )}
              {(() => {
                const z = climateZoneForLat(hive.latitude);
                const s = z ? zoneStyle(z) : null;
                return s ? (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${s.chip}`}
                    title="Climate zone — drives reminders & forecast"
                    data-testid="hive-detail-zone-chip"
                  >
                    <span className={`w-1 h-1 rounded-full ${s.dot}`} aria-hidden />
                    {s.label}
                  </span>
                ) : null;
              })()}
              <span>{hive.hive_type}</span>
              {hive.species && (
                <span
                  className="inline-flex items-center gap-1"
                  title="Species / subspecies"
                  data-testid="hive-detail-species"
                >
                  <Bug className="w-3.5 h-3.5" /> {hive.species}
                </span>
              )}
              {hive.queen_color && <span className="inline-flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> {hive.queen_color}{hive.queen_year ? ` · ${hive.queen_year}` : ""}</span>}
              {hive.installation_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {hive.installation_date}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <ShareLinkDialog scope="hive" hiveId={id} label="Share hive" />
            <Dialog open={journalOpen} onOpenChange={setJournalOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="add-journal-button">
                  <Plus className="w-4 h-4 mr-2" /> New entry
                </Button>
              </DialogTrigger>
              <JournalFormDialog hiveId={id} onSaved={() => { setJournalOpen(false); load(); }} />
            </Dialog>
            <Dialog open={!!editingJournal} onOpenChange={(o) => { if (!o) setEditingJournal(null); }}>
              {editingJournal && (
                <JournalFormDialog
                  hiveId={id}
                  journal={editingJournal}
                  onSaved={() => { setEditingJournal(null); load(); }}
                />
              )}
            </Dialog>
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-stone-300 text-stone-700" data-testid="edit-hive-button">
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
              </DialogTrigger>
              <HiveFormDialog
                hive={hive}
                onSaved={(updated) => {
                  setEditOpen(false);
                  if (updated) setHive(updated);
                  else load();
                }}
              />
            </Dialog>
            <Button variant="outline" className="rounded-full border-stone-300 text-stone-600 hover:text-red-700 hover:border-red-200" onClick={del} data-testid="delete-hive-button">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {hive.notes && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">{hive.notes}</div>
      )}

      <HiveLocationCard
        hive={hive}
        canEdit={isOwner || role === "editor"}
        onUpdated={(updated) => setHive(updated)}
      />

      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className="bg-stone-100 rounded-full p-1">
          <TabsTrigger value="journal" className="rounded-full" data-testid="tab-journal">Journal</TabsTrigger>
          <TabsTrigger value="photos" className="rounded-full" data-testid="tab-photos">Photos</TabsTrigger>
          <TabsTrigger value="treatments" className="rounded-full" data-testid="tab-treatments">Treatments</TabsTrigger>
          <TabsTrigger value="trends" className="rounded-full" data-testid="tab-trends">Trends</TabsTrigger>
          <TabsTrigger value="weather" className="rounded-full" data-testid="tab-weather">Forecast</TabsTrigger>
        </TabsList>

        <TabsContent value="journal" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-medium">Inspection entries</h3>
            <Button onClick={() => setJournalOpen(true)} size="sm" className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="journal-tab-add">
              <Plus className="w-4 h-4 mr-1.5" /> New entry
            </Button>
          </div>
          {journals.length === 0 ? (
            <EmptyInspection onAdd={() => setJournalOpen(true)} />
          ) : (
            <div className="space-y-4" data-testid="journal-list">
              {journals.map((j) => (
                <article
                  key={j.id}
                  onClick={canEditEntry(j) ? () => setEditingJournal(j) : undefined}
                  className={`bg-white border border-stone-200 rounded-2xl p-6 ${canEditEntry(j) ? "cursor-pointer transition-colors hover:border-amber-300 hover:shadow-sm" : ""}`}
                  title={canEditEntry(j) ? "Click to view / edit this entry" : undefined}
                  data-testid={`journal-entry-${j.id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
                      {format(parseISO(j.date), "EEEE, MMM d, yyyy")}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-1 rounded-full bg-stone-100 text-stone-700">{j.weather || "—"}</span>
                      <span className="px-2 py-1 rounded-full bg-stone-100 text-stone-700">{j.temperature || "—"}</span>
                      <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-900">{j.mood}</span>
                      {canEditEntry(j) && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingJournal(j); }}
                            className="ml-1 p-1.5 rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                            title="Edit entry"
                            aria-label="Edit entry"
                            data-testid={`journal-edit-${j.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteJournal(j.id); }}
                            className="p-1.5 rounded-full text-stone-400 hover:bg-red-50 hover:text-red-600"
                            title="Delete entry"
                            aria-label="Delete entry"
                            data-testid={`journal-delete-${j.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-stone-800 mt-3 whitespace-pre-wrap">{j.observations}</p>
                  {j.actions_taken && (
                    <div className="mt-3 text-sm text-stone-600">
                      <span className="font-medium text-stone-700">Actions: </span>{j.actions_taken}
                    </div>
                  )}
                  {j.photos?.length > 0 && (
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-4">
                      {j.photos.map((p) => (
                        <img key={p} src={p} alt="" className="aspect-square object-cover rounded-lg border border-stone-200" />
                      ))}
                    </div>
                  )}
                  {j.videos?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid={`journal-videos-${j.id}`}>
                      {j.videos.map((v, vi) => (
                        <button
                          key={`${v.public_id}-${vi}`}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setActiveVideo(v); }}
                          className="relative group w-32 h-20 rounded-lg overflow-hidden border border-stone-200 bg-stone-100"
                          data-testid={`journal-video-thumb-${j.id}-${vi}`}
                          aria-label="Play video"
                        >
                          {v.poster_url && <img src={v.poster_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
                          <span className="absolute inset-0 grid place-items-center bg-black/30 group-hover:bg-black/40 transition-colors">
                            <svg viewBox="0 0 24 24" className="w-7 h-7 text-white drop-shadow" fill="currentColor"><polygon points="7,5 19,12 7,19" /></svg>
                          </span>
                          {v.duration > 0 && (
                            <span className="absolute bottom-1 right-1 text-[10px] font-medium text-white bg-black/60 px-1.5 py-0.5 rounded">
                              {Math.round(v.duration)}s
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  <div onClick={(e) => e.stopPropagation()}>
                    <CommentsThread targetType="journal" targetId={j.id} currentUserId={user?.id} role={role} />
                  </div>
                </article>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="photos" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-medium">Photos</h3>
            <Button onClick={() => setJournalOpen(true)} size="sm" className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="photos-tab-add">
              <Plus className="w-4 h-4 mr-1.5" /> Add photos
            </Button>
          </div>
          {allPhotos.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-12 text-center text-stone-500">
              <ImageIcon className="w-8 h-8 mx-auto mb-3 text-stone-400" />
              <p>No photos yet.</p>
              <p className="text-sm mt-1">Photos are attached to journal entries.</p>
              <Button onClick={() => setJournalOpen(true)} className="mt-4 rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="photos-empty-add">
                <Plus className="w-4 h-4 mr-1.5" /> Add an entry with photos
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="photo-gallery">
              {allPhotos.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { if (canEditEntry(p.journal)) setEditingJournal(p.journal); }}
                  className={`group relative block aspect-square ${canEditEntry(p.journal) ? "cursor-pointer" : "cursor-default"}`}
                  title={canEditEntry(p.journal) ? "Open the entry this photo belongs to" : undefined}
                  data-testid={`photo-${i}`}
                >
                  <img src={p.url} alt="" className="w-full h-full object-cover rounded-xl border border-stone-200 transition group-hover:brightness-95" />
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="treatments" className="mt-6">
          <TreatmentsTab hiveId={id} />
        </TabsContent>

        <TabsContent value="trends" className="mt-6">
          <HiveTrendsTab hiveId={id} highlightMonth={activeTab === "trends" ? monthFromUrl : null} />
        </TabsContent>

        <TabsContent value="weather" className="mt-6">
          <HiveWeatherTab hiveId={id} />
        </TabsContent>
      </Tabs>

      <HiveCoKeepers hiveId={id} isOwner={isOwner} />

      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <h2 className="font-display text-lg font-medium mb-1">Hive discussion</h2>
        <p className="text-xs text-stone-500 mb-3">Co-keepers can chat about this hive. Use @name to mention someone.</p>
        <CommentsThread targetType="hive" targetId={id} currentUserId={user?.id} role={role} />
      </div>

      {activeVideo && <VideoLightbox video={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
}

function EmptyInspection({ onAdd }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-12 text-center">
      <div className="font-display text-2xl font-medium">No inspections yet</div>
      <p className="text-stone-500 mt-2">Log your first visit to keep a record of this colony's journey.</p>
      {onAdd && (
        <Button onClick={onAdd} className="mt-5 rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="journal-empty-add">
          <Plus className="w-4 h-4 mr-1.5" /> New entry
        </Button>
      )}
    </div>
  );
}

function JournalFormDialog({ hiveId, journal, onSaved }) {
  const { user: prefs } = useAuth();
  const tempPlaceholder = prefs?.temperature_unit === "C" ? "22°C" : "72°F";
  const today = new Date().toISOString().slice(0, 10);
  const isEdit = !!journal;
  const [form, setForm] = useState({
    date: (journal?.date || today).slice(0, 10),
    weather: journal?.weather || "",
    temperature: journal?.temperature || "",
    observations: journal?.observations || "",
    actions_taken: journal?.actions_taken || "",
    mood: journal?.mood || "calm",
    photos: journal?.photos || [],
    videos: journal?.videos || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const { secure_url } = await uploadToCloudinary(f);
        urls.push(secure_url);
      }
      setForm((prev) => ({ ...prev, photos: [...prev.photos, ...urls] }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/journals/${journal.id}`, { ...form });
        toast.success("Entry updated");
      } else {
        await api.post("/journals", { ...form, hive_id: hiveId });
        toast.success("Entry saved");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium">{isEdit ? "Edit inspection entry" : "New inspection entry"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        <VoiceJournalRecorder
          onResult={(v) =>
            setForm((prev) => ({
              ...prev,
              observations: v.observations || prev.observations,
              actions_taken: v.actions_taken || prev.actions_taken,
              mood: v.mood || prev.mood,
              weather: v.weather || prev.weather,
              temperature: v.temperature || prev.temperature,
            }))
          }
        />
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="journal-date-input" />
          </div>
          <div>
            <Label>Weather</Label>
            <Input value={form.weather} maxLength={200} onChange={(e) => setForm({ ...form, weather: e.target.value })} placeholder="Sunny" data-testid="journal-weather-input" />
          </div>
          <div>
            <Label>Temp</Label>
            <Input value={form.temperature} maxLength={40} onChange={(e) => setForm({ ...form, temperature: e.target.value })} placeholder={tempPlaceholder} data-testid="journal-temp-input" />
          </div>
        </div>
        <div>
          <Label>Colony mood</Label>
          <Select value={form.mood} onValueChange={(v) => setForm({ ...form, mood: v })}>
            <SelectTrigger data-testid="journal-mood-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="calm">Calm</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="defensive">Defensive</SelectItem>
              <SelectItem value="irritated">Irritated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Observations</Label>
          <Textarea rows={4} maxLength={10000} value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} placeholder="Brood pattern, queen sighting, frames of honey..." data-testid="journal-observations-input" />
        </div>
        <div>
          <Label>Actions taken</Label>
          <Textarea rows={2} maxLength={10000} value={form.actions_taken} onChange={(e) => setForm({ ...form, actions_taken: e.target.value })} placeholder="Added super, reversed brood box..." data-testid="journal-actions-input" />
        </div>
        <div>
          <Label>Photos</Label>
          <input type="file" accept="image/*" multiple onChange={handleFiles} className="block mt-1.5 text-sm" data-testid="journal-photo-input" />
          {uploading && <div className="text-xs text-stone-500 mt-1">Uploading…</div>}
          {form.photos.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {form.photos.map((p) => (
                <img key={p} src={p} alt="" className="aspect-square object-cover rounded-lg border border-stone-200" />
              ))}
            </div>
          )}
        </div>
        <div>
          <Label>Videos</Label>
          <VideoUploadField
            videos={form.videos}
            onChange={(videos) => setForm((prev) => ({ ...prev, videos }))}
            testid="journal-video-field"
          />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving || !form.observations} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="journal-save-button">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Save entry"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
